import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, DatePicker, Select, Upload, Button, Switch } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useFirestore } from '../../contexts/FirestoreContext';
import { useMilestones } from '../../contexts/MilestoneDataContext';
import { collection, query, where, getDocs, doc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

import moment from 'moment';

const { TextArea } = Input;

export default function MilestoneModal({ visible, onClose, onSave, milestone, incidentId }) {
  const [form] = Form.useForm();
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  
  // 🎯 USE SHARED MILESTONE DATA FOR PREREQUISITES
  const { milestones: allMilestones } = useMilestones({ incidentId, includeDeleted: false });
  
  // Filter out current milestone for prerequisites
  const availableMilestones = allMilestones.filter(m => m.id !== milestone?.id);

  // Initialize form with milestone data
  useEffect(() => {
    if (milestone) {
      form.setFieldsValue({
        title: milestone.title,
        description: milestone.description,
        date: milestone.dueDate ? moment(milestone.dueDate.toDate ? milestone.dueDate.toDate() : milestone.dueDate) : null,
        status: milestone.status,
        prerequisites: milestone.prerequisites || [],
        dependencies: milestone.dependencies || [],
        isSystemGenerated: milestone.isSystemGenerated || false
      });
      setFileList(milestone.attachments || []);
    } else {
      form.resetFields();
      setFileList([]);
    }
  }, [milestone, form]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      // Handle file uploads
      const uploadedFiles = await Promise.all(
        fileList.map(async file => {
          if (file.originFileObj) {
            const storage = getStorage();
            const storageRef = ref(storage, `milestones/${incidentId}/${file.name}`);
            await uploadBytes(storageRef, file.originFileObj);
            const url = await getDownloadURL(storageRef);
            return { name: file.name, url };
          }
          return file;
        })
      );

      const milestoneData = {
        title: values.title,
        description: values.description,
        dueDate: Timestamp.fromDate(values.date.toDate()),
        status: values.status,
        prerequisites: values.prerequisites || [],
        dependencies: values.dependencies || [],
        attachments: uploadedFiles,
        incidentId,
        orgId: currentUser.orgId,
        createdBy: currentUser.uid,
        createdAt: milestone?.createdAt || Timestamp.now(),
        updatedAt: Timestamp.now(),
        deleted: false,
        isSystemGenerated: values.isSystemGenerated || false
      };

      if (milestone) {
        await updateDoc(doc(db, 'c3_milestones', milestone.id), milestoneData);
      } else {
        await setDoc(doc(collection(db, 'c3_milestones')), milestoneData);
      }

      onSave(milestoneData);
      onClose();
    } catch (error) {
      console.error('Error saving milestone:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={milestone ? 'Edit Milestone' : 'Add Milestone'}
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={800}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: 'Not Started',
          isSystemGenerated: false
        }}
        preserve={false}
      >
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: 'Please enter a title' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true, message: 'Please enter a description' }]}
        >
          <TextArea rows={4} />
        </Form.Item>

        <Form.Item
          name="date"
          label="Milestone Date & Time"
          rules={[{ required: true, message: 'Please select a date and time' }]}
        >
          <DatePicker showTime />
        </Form.Item>

        <Form.Item
          name="status"
          label="Status"
          rules={[{ required: true, message: 'Please select a status' }]}
        >
          <Select>
            <Select.Option value="Not Started">Not Started</Select.Option>
            <Select.Option value="In Progress">In Progress</Select.Option>
            <Select.Option value="Complete">Complete</Select.Option>
            <Select.Option value="Blocked">Blocked</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="prerequisites"
          label="Prerequisites"
        >
          <Select mode="multiple" placeholder="Select prerequisite milestones">
            {availableMilestones.map(milestone => (
              <Select.Option key={milestone.id} value={milestone.id}>
                {milestone.title}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="dependencies"
          label="Dependencies"
        >
          <Select mode="multiple" placeholder="Select dependent milestones">
            {availableMilestones.map(milestone => (
              <Select.Option key={milestone.id} value={milestone.id}>
                {milestone.title}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="isSystemGenerated"
          label="System Generated"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          label="Attachments"
        >
          <Upload
            fileList={fileList}
            onChange={({ fileList }) => setFileList(fileList)}
            beforeUpload={() => false}
            multiple
          >
            <Button icon={<UploadOutlined />}>Upload Files</Button>
          </Upload>
        </Form.Item>
      </Form>
    </Modal>
  );
} 